import type { RootOptions } from "react-dom/client";
import type { RuntimeConfig } from "../config";

/**
 * Frontend observability, driven by /config.json (see lib/config.ts). Both parts are loaded lazily as separate
 * chunks, so a build/config without them costs nothing:
 * - OpenTelemetry web SDK (`otlpEndpoint`): document load + fetch spans, W3C traceparent on /v1 calls,
 *   OTLP/HTTP export to the same-origin collector route (`/otlp` → rate-limited proxy → collector)
 * - GlitchTip via @sentry/react (`sentryDsn`): uncaught errors and React render errors, scrubbed before sending
 */
export interface WebTelemetry {
  tracing: boolean;
  errors: boolean;
  /** Pass to createRoot: React 19 error hooks report to GlitchTip. */
  rootOptions: RootOptions;
}

export async function initWebTelemetry(cfg: RuntimeConfig): Promise<WebTelemetry> {
  const out: WebTelemetry = { tracing: false, errors: false, rootOptions: {} };
  const jobs: Promise<void>[] = [];
  if (cfg.otlpEndpoint) {
    jobs.push(
      import("./otel")
        .then((m) => {
          m.startTracing(m.tracingOptions(cfg, location.origin));
          out.tracing = true;
        })
        .catch((e: unknown) => console.warn("telemetry: tracing disabled", e)),
    );
  }
  if (cfg.sentryDsn) {
    jobs.push(
      import("./sentry")
        .then((m) => {
          out.rootOptions = m.startErrorTracking(cfg);
          out.errors = true;
        })
        .catch((e: unknown) => console.warn("telemetry: error tracking disabled", e)),
    );
  }
  await Promise.all(jobs);
  return out;
}
